import React, { useEffect, useState } from 'react'
import { Typography, Select, Input, message } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import 'src/Pages/LibraryPages/librarypages.scss'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select

const CompletedPhase = () => {
  const { phaseId, lessonLearnedId } = useParams()
  const navigate = useNavigate()
  const [dataLoading, setDataLoading] = useState(true)
  const [projectInfo, setProjectInfo] = useState(null)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [submitLoading, setSubmitLoading] = useState(false)
  const token = localStorage.getItem('token')

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API}/InternLessonLearned/phase?lessonLearnedId=${lessonLearnedId}&phaseId=${phaseId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: '*/*',
            },
          },
        )
        setProjectInfo(response.data)
      } catch (error) {
        message.error('Failed to fetch project data')
      }
      setDataLoading(false)
    }

    fetchProjectData()
  }, [phaseId, lessonLearnedId])

  // Answer change handler
  const handleAnswerChange = (question, value) => {
    setAnswers((prev) => ({
      ...prev,
      [question]: value,
    }))

    setErrors((prev) => ({
      ...prev,
      [question]: value ? '' : 'This field is required',
    }))
  }

  // Validation Check
  const validateFields = () => {
    const newErrors = {}
    projectInfo?.questions.forEach((q) => {
      if (!answers[q.question] || answers[q.question].trim() === '') {
        newErrors[q.question] = 'This field is required'
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Submit API Call
  const handleSubmit = async () => {
    if (!validateFields()) {
      message.error('Please fill all required fields!')
      return
    }

    const formattedAnswers = Object.entries(answers).map(([question, answer]) => ({
      question,
      answer,
    }))

    const payload = {
      lessonLearnedId: Number(lessonLearnedId),
      phaseId: Number(phaseId),
      answers: formattedAnswers,
    }

    try {
      setSubmitLoading(true)
      await axios.post(
        `${process.env.REACT_APP_BASE_URL_API}/InternLessonLearned/submit`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      message.success('Answers submitted successfully!')
      navigate(-1) // Navigate back
    } catch (error) {
      message.error('Failed to submit answers')
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <div className="lpg">
      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <path d="M9 14l2 2 4-4" />
            </svg>
          </span>
          <div>
            <h1 className="lpg__title">Completed Phase</h1>
            <p className="lpg__sub">Phase completion details.</p>
          </div>
        </div>
        <button type="button" className="lpg__back" onClick={() => navigate(-1)}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M11 18l-6-6 6-6" />
          </svg>{' '}
          Back
        </button>
      </div>

      {dataLoading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : projectInfo ? (
        <div className="lpg__panel-box">
          {/* Project Information Section */}
          <div
            style={{
              background: '#f8faff',
              border: '1px solid #e6ebf3',
              padding: '18px 20px',
              borderRadius: '12px',
              marginBottom: '20px',
            }}
          >
            <Title level={4}>Project Information</Title>
            <Text strong>Project Name:</Text> <Text>{projectInfo.projectName}</Text> <br />
            <Text strong>Group Name:</Text> <Text>{projectInfo.groupName}</Text> <br />
            <Text strong>Phase:</Text> <Text>{projectInfo.phaseName}</Text> <br />
          </div>

          {/* Lesson Learned Questions Section */}
          <Title level={4} style={{ marginTop: '20px' }}>
            Lesson Learned Questions
          </Title>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {projectInfo.questions.map((question, index) => (
              <div
                key={index}
                style={{
                  flex: question.question.length < 40 ? '1 1 45%' : '1 1 100%',
                  minWidth: '300px',
                }}
              >
                <Text strong>{question.question}</Text>
                {question.isSelectableAnswer ? (
                  <Select
                    style={{ width: '100%', marginTop: '5px' }}
                    placeholder="Select an option"
                    onChange={(value) => handleAnswerChange(question.question, value)}
                    value={answers[question.question] || undefined}
                  >
                    {question.answerOptions.map((option, i) => (
                      <Option key={i} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <TextArea
                    rows={3}
                    placeholder="Enter your response..."
                    style={{ marginTop: '5px' }}
                    onChange={(e) => handleAnswerChange(question.question, e.target.value)}
                    value={answers[question.question] || ''}
                  />
                )}
                {errors[question.question] && (
                  <Text type="danger">{errors[question.question]}</Text>
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <button
            type="button"
            className="lpg__btn-primary"
            style={{ marginTop: '20px' }}
            onClick={handleSubmit}
            disabled={submitLoading}
          >
            {submitLoading ? 'Please wait...' : 'Submit'}
          </button>
        </div>
      ) : (
        <div className="lpg__panel-box">
          <Text type="danger">No project data found.</Text>
        </div>
      )}
    </div>
  )
}

export default CompletedPhase
